import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PauseIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Progress from '@/components/ui/Progress';
import clsx from 'clsx';

// Mock data
const mockJobMonitor = {
  id: 'job_abc123',
  title: 'Le Petit Prince',
  voice: 'Cath Brunet',
  status: 'running',
  startedAt: '10:30 AM',
  duration: '12m 34s',
  progress: 57,
  currentStep: 4,
  totalSteps: 7,
};

const pipelineSteps = [
  { name: 'PDF Ingest', status: 'completed', duration: '2m', icon: '📄' },
  { name: 'Image Processor', status: 'completed', duration: '5m', icon: '🖼️' },
  { name: 'Chapter Detector', status: 'completed', duration: '30s', icon: '📑' },
  { name: 'TTS Synthesis', status: 'running', duration: '5m', icon: '🔊' },
  { name: 'Audio Assembler', status: 'pending', duration: '--', icon: '🎵' },
  { name: 'Credits Generator', status: 'pending', duration: '--', icon: '📝' },
  { name: 'QA Finalize', status: 'pending', duration: '--', icon: '✅' },
];

const stats = {
  pages_processed: 156,
  chapters_detected: 18,
  tts_batches: { total: 30, completed: 12, in_progress: 4 },
  events_emitted: 234,
  llm_calls: 156,
  elevenlabs_calls: 47,
  est_cost: '$2.34',
  files_created: 312,
};

const events = [
  { time: '10:42:34', event: 'synthesize_tts_batch_end', detail: 'batch_12 completed (3.2s)', type: 'success' },
  { time: '10:42:31', event: 'synthesize_tts_batch_start', detail: 'batch_13 starting...', type: 'info' },
  { time: '10:42:28', event: 'synthesize_tts_batch_end', detail: 'batch_11 completed (2.9s)', type: 'success' },
  { time: '10:42:26', event: 'elevenlabsapi_call', detail: 'chunk_47: 1,234 chars → mp3', type: 'info' },
  { time: '10:42:20', event: 'synthesize_tts_batch_start', detail: 'batch_12 starting...', type: 'info' },
  { time: '10:35:12', event: 'chapter_detector_end', detail: '18 chapters detected', type: 'success' },
  { time: '10:34:42', event: 'chapter_detector_start', detail: 'Beginning detection...', type: 'info' },
  { time: '10:34:40', event: 'image_processor_png_end', detail: '156 pages processed', type: 'success' },
];

function getStepStatusStyle(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-success-100 border-success-300 text-success-700';
    case 'running':
      return 'bg-primary-100 border-primary-300 text-primary-700 animate-pulse';
    case 'pending':
      return 'bg-slate-100 border-slate-200 text-slate-500';
    default:
      return 'bg-slate-100 border-slate-200 text-slate-500';
  }
}

export default function TTSJobMonitorPage() {
  const { jobId } = useParams();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [eventFilter, setEventFilter] = useState('all');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to={`/tts/jobs/${jobId}`}
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Job
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-bold text-slate-800">📖 {mockJobMonitor.title}</h1>
            <Badge variant="primary">Processing</Badge>
          </div>
          <p className="text-slate-500">
            Voice: {mockJobMonitor.voice} | Started: {mockJobMonitor.startedAt} | Duration: {mockJobMonitor.duration} (running)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Auto-refresh:</span>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={clsx(
              'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
              autoRefresh ? 'bg-success-100 text-success-700' : 'bg-slate-100 text-slate-600'
            )}
          >
            {autoRefresh ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* Pipeline Flow Diagram */}
      <Card>
        <CardHeader title="Pipeline Flow" />
        <CardBody>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {pipelineSteps.map((step, index) => (
              <div key={step.name} className="flex items-center">
                <div
                  className={clsx(
                    'px-4 py-3 rounded-xl border-2 text-center min-w-[100px]',
                    getStepStatusStyle(step.status)
                  )}
                >
                  <div className="text-2xl mb-1">{step.icon}</div>
                  <p className="text-xs font-medium">{step.name}</p>
                  <p className="text-xs opacity-75">{step.duration}</p>
                </div>
                {index < pipelineSteps.length - 1 && (
                  <div className="w-8 h-0.5 bg-slate-200 mx-1" />
                )}
              </div>
            ))}
          </div>

          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-500">Progress</span>
              <span className="font-medium text-slate-700">Step {mockJobMonitor.currentStep}/{mockJobMonitor.totalSteps}</span>
            </div>
            <Progress value={mockJobMonitor.progress} showLabel variant="primary" size="lg" />
          </div>
        </CardBody>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.pages_processed}</p>
            <p className="text-sm text-slate-500">📄 Pages Processed</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.chapters_detected}</p>
            <p className="text-sm text-slate-500">📑 Chapters Detected</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.tts_batches.completed}/{stats.tts_batches.total}</p>
            <p className="text-sm text-slate-500">🎵 TTS Batches</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.events_emitted}</p>
            <p className="text-sm text-slate-500">🔄 Events Emitted</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.llm_calls}</p>
            <p className="text-sm text-slate-500">🤖 LLM Calls</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.elevenlabs_calls}</p>
            <p className="text-sm text-slate-500">🔊 ElevenLabs Calls</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.est_cost}</p>
            <p className="text-sm text-slate-500">💰 Est. Cost</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.files_created}</p>
            <p className="text-sm text-slate-500">💾 Files Created</p>
          </CardBody>
        </Card>
      </div>

      {/* TTS Batch Progress */}
      <Card>
        <CardHeader title="TTS Batch Progress" />
        <CardBody>
          <div className="mb-4">
            <div className="flex gap-1">
              {Array.from({ length: stats.tts_batches.total }).map((_, i) => (
                <div
                  key={i}
                  className={clsx(
                    'h-4 flex-1 rounded-sm',
                    i < stats.tts_batches.completed
                      ? 'bg-success-500'
                      : i < stats.tts_batches.completed + stats.tts_batches.in_progress
                      ? 'bg-primary-400 animate-pulse'
                      : 'bg-slate-200'
                  )}
                />
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {stats.tts_batches.completed}/{stats.tts_batches.total} complete • {stats.tts_batches.in_progress} active • Avg batch: 3.1s • Est. remaining: 8m
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Live Event Timeline */}
      <Card>
        <CardHeader
          title="Live Event Timeline"
          action={
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
            >
              <option value="all">All Events</option>
              <option value="tts">TTS Events</option>
              <option value="success">Success Only</option>
            </select>
          }
        />
        <CardBody>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {events.map((event, index) => (
              <div
                key={index}
                className="flex items-start gap-3 text-sm"
              >
                <span className="text-slate-400 font-mono w-20">{event.time}</span>
                <span
                  className={clsx(
                    'w-2 h-2 mt-1.5 rounded-full',
                    event.type === 'success' ? 'bg-success-500' : 'bg-primary-400'
                  )}
                />
                <div>
                  <span className="font-medium text-slate-700">{event.event}</span>
                  <span className="text-slate-500 ml-2">{event.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" leftIcon={<PauseIcon className="w-4 h-4" />}>
          Pause Updates
        </Button>
        <Button variant="outline" leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
          Retry Failed
        </Button>
        <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
          Export Events
        </Button>
        <Button variant="danger" leftIcon={<XMarkIcon className="w-4 h-4" />}>
          Cancel Job
        </Button>
      </div>
    </div>
  );
}

